import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { Copy, SkipForward, RotateCcw, Loader } from 'lucide-react';
import { useHabitTemplates } from '../../hooks/useHabitTemplates';
import { useAuth } from '../../contexts/AuthContext';
/**
 * Instance-level actions for steps in recurring habits
 * Allows users to skip, duplicate/customize, or reset steps per instance
 */
export function StepInstanceActions({ stepId, habitGroupId, instanceDate, isTemplate, templateStepId, onActionComplete, isSkipped = false, isDuplicated = false, }) {
    const { user } = useAuth();
    const habitTemplates = useHabitTemplates();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    if (isTemplate || !user) {
        return null; // Don't show actions for template steps
    }
    const handleSkip = async () => {
        if (isSkipped)
            return; // Already skipped
        try {
            setIsLoading(true);
            setError(null);
            await habitTemplates.skipInstance(stepId, instanceDate, user.id);
            onActionComplete?.();
        }
        catch (err) {
            setError('Failed to skip instance');
            console.error('Skip error:', err);
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleDuplicate = async () => {
        if (isDuplicated || !templateStepId)
            return;
        try {
            setIsLoading(true);
            setError(null);
            await habitTemplates.duplicateAndCustomize(templateStepId, instanceDate, habitGroupId, user.id);
            onActionComplete?.();
        }
        catch (err) {
            setError('Failed to duplicate step');
            console.error('Duplicate error:', err);
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleReset = async () => {
        if (!isDuplicated || !templateStepId)
            return;
        try {
            setIsLoading(true);
            setError(null);
            await habitTemplates.resetToTemplate(stepId, instanceDate, user.id);
            onActionComplete?.();
        }
        catch (err) {
            setError('Failed to reset step');
            console.error('Reset error:', err);
        }
        finally {
            setIsLoading(false);
        }
    };
    return (_jsxs("div", { className: "flex items-center gap-2", children: [error && _jsx("span", { className: "text-xs text-red-500", children: error }), !isSkipped && (_jsx("button", { onClick: handleSkip, disabled: isLoading, className: "p-1 rounded hover:bg-orange-500/20 transition-colors disabled:opacity-50", title: "Skip this instance", children: isLoading ? (_jsx(Loader, { className: "w-4 h-4 animate-spin text-orange-500" })) : (_jsx(SkipForward, { className: "w-4 h-4 text-orange-500" })) })), !isDuplicated && templateStepId && (_jsx("button", { onClick: handleDuplicate, disabled: isLoading, className: "p-1 rounded hover:bg-blue-500/20 transition-colors disabled:opacity-50", title: "Duplicate & customize for this day", children: isLoading ? (_jsx(Loader, { className: "w-4 h-4 animate-spin text-blue-500" })) : (_jsx(Copy, { className: "w-4 h-4 text-blue-500" })) })), isDuplicated && (_jsx("button", { onClick: handleReset, disabled: isLoading, className: "p-1 rounded hover:bg-purple-500/20 transition-colors disabled:opacity-50", title: "Reset to template", children: isLoading ? (_jsx(Loader, { className: "w-4 h-4 animate-spin text-purple-500" })) : (_jsx(RotateCcw, { className: "w-4 h-4 text-purple-500" })) })), isSkipped && (_jsx("span", { className: "text-xs px-2 py-1 rounded bg-orange-500/10 text-orange-600", children: "Skipped" })), isDuplicated && (_jsx("span", { className: "text-xs px-2 py-1 rounded bg-blue-500/10 text-blue-600", children: "Customized" }))] }));
}
